import React, { useMemo } from 'react'
import { flushSync } from 'react-dom'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export { arrayMove }

function useSortSensors() {
  return useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  )
}

/**
 * Drop-in item wrapper for dnd-kit sortable lists/grids.
 * Renders a plain element (default: div) with drag listeners attached.
 *
 * Prefer making the layout cell itself sortable (e.g. `as={Grid}`) so the
 * whole card moves — wrapping only inner content causes a snap-back on drop.
 */
export function SortableItem({
  id,
  children,
  as: Component = 'div',
  className,
  style,
  disabled = false,
  ...rest
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled,
    // Avoid post-drop layout animation that fights the React reorder.
    animateLayoutChanges: () => false,
  })

  return (
    <Component
      ref={setNodeRef}
      className={className}
      style={{
        ...style,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.85 : undefined,
        zIndex: isDragging ? 2 : undefined,
        cursor: disabled ? undefined : 'grab',
      }}
      {...attributes}
      {...listeners}
      {...rest}
    >
      {children}
    </Component>
  )
}

/**
 * Generic sortable list/grid.
 * `items` must be stable string/number ids.
 * `onSortEnd({ oldIndex, newIndex  })` matches react-sortable-hoc shape.
 */
export function SortableList({
  items,
  onSortEnd,
  strategy = 'vertical',
  disabled = false,
  children,
}) {
  const sensors = useSortSensors()
  const ids = useMemo(
    () => items.map((item) => String(item)),
    [items],
  )
  const sortingStrategy =
    strategy === 'xy' ? rectSortingStrategy : verticalListSortingStrategy

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id || disabled) return
    const oldIndex = ids.indexOf(String(active.id))
    const newIndex = ids.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return
    // Commit parent reorder before dnd-kit clears drag transforms,
    // otherwise cards visibly snap back then jump into place.
    flushSync(() => {
      onSortEnd?.({ oldIndex, newIndex  })
    })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={ids}
        strategy={sortingStrategy}
        disabled={disabled}
      >
        {children}
      </SortableContext>
    </DndContext>
  )
}
